import { format as formatDateFns } from 'date-fns'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, ArrowUpRight, Lock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn, formatDateHuman } from '@/lib/utils'
import { rangeToSearch, type UseReportRange } from '@/hooks/useReportRange'
import type { CurrentDayStatus, ExpenseIntelligenceReport, ExpenseSummary, PnLReport } from '@/types'
import { ExpenseCategoryShelf } from './ExpenseCategoryShelf'
import { buildCategoryChips } from './expenseDashboardLogic'
import { KpiStrip, TermLabel, useMoney } from './moneyUi'
import { PeriodResultChart, type ResultPoint } from './PeriodResultChart'
import { PriceChangesCard } from './PriceChangesCard'
import { useExpenseCategoryDefs } from './use-expense-category-defs'

type Props = {
  /** Today's live numbers — day-ops endpoint, so admin/manager only. */
  dayStatus?: CurrentDayStatus
  /** P&L for the period bar (COGS, net profit, refunds). */
  pnl?: PnLReport
  /** Ledger totals for the period — the same numbers the ledger tab shows. */
  summary?: ExpenseSummary
  /** Sales vs expenses over the period, the prior-window comparison, entry mix, cash closing. */
  intel?: ExpenseIntelligenceReport
  periodLabel: string
  range: UseReportRange
  onDrillCategory: (slug: string) => void
  onOpenLedger: () => void
  onOpenPnL: () => void
}

/**
 * Overview (F-FE-N129): the period's money in one screen, every number a
 * door. Same period bar as the ledger and the P&L, so the figure you click
 * and the rows you land on describe the same window.
 *
 * F-FE-N164: it shares the P&L's strip, chart and colour rule, so the three
 * tabs read as one page rather than three.
 */
export function ExpenseOverviewTab({
  dayStatus,
  pnl,
  summary,
  intel,
  periodLabel,
  range,
  onDrillCategory,
  onOpenLedger,
  onOpenPnL,
}: Props) {
  const money = useMoney()
  const navigate = useNavigate()
  const { data: defs = [] } = useExpenseCategoryDefs()

  const kpis = intel?.kpis
  const opex = summary?.grand_total ?? kpis?.total_expenses ?? 0
  const stock = summary?.stock_total ?? kpis?.inventory_spend ?? 0
  const sales = kpis?.total_sales ?? 0
  const cogs = pnl?.summary?.cogs ?? 0
  const coverage = pnl?.summary?.cost_coverage_pct ?? 0
  const cogsBasis = pnl?.summary?.cogs_basis
  const stockHelp =
    cogsBasis === 'purchases'
      ? 'What was spent buying stock in this period. It is not an operating expense: it is your cost of goods, counted on the day it was bought.'
      : cogsBasis === 'mixed'
        ? 'What was spent buying stock in this period. It is not an operating expense: it reaches the profit and loss as cost of goods.'
        : 'What was spent buying stock in this period. It is not an operating expense: it reaches the profit and loss as cost of goods, when the stock is sold.'
  const netProfit = pnl?.summary?.net_profit ?? 0
  const refunds = pnl?.summary?.refunds_issued ?? 0
  const netAfterRefunds = pnl?.summary?.net_profit_after_refunds
  const result = refunds > 0.005 && netAfterRefunds != null ? netAfterRefunds : netProfit
  const pctOfSales = (n: number) => (sales > 0 ? `${((n / sales) * 100).toFixed(1)}% of sales` : 'No sales in this period')

  // The biggest stock category is where a click on "Stock purchases" lands.
  const stockChip = buildCategoryChips(summary, defs).find(c => c.isStock)

  const trend: ResultPoint[] = (intel?.daily_trend ?? []).map(p => ({
    tick: formatDateFns(new Date(`${p.date}T12:00:00`), 'd MMM'),
    label: formatDateHuman(`${p.date}T12:00:00`),
    sales: p.sales,
    opex: p.expenses,
    result: p.net,
  }))
  const hasActivity = trend.some(p => Math.abs(p.sales) > 0.005 || Math.abs(p.opex) > 0.005)

  const openReports = (tab: 'items' | 'day-close') =>
    navigate({ to: '/admin/reports', search: { tab, ...rangeToSearch(range) } })

  return (
    <div className="space-y-6">
      {dayStatus && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 p-4 sm:px-5">
            <div>
              <p className="text-sm font-medium">Today</p>
              <p className="text-xs text-muted-foreground">{dayStatus.is_closed ? 'Day closed' : 'Day open'}</p>
            </div>
            <TodayStat label="Sales" value={money.whole(dayStatus.total_sales ?? 0)} />
            <TodayStat label="Cost of goods" value={money.whole(dayStatus.cogs ?? 0)} />
            <TodayStat label="Expenses" value={money.whole(dayStatus.total_expenses ?? 0)} />
            <TodayStat label="Net" value={money.whole(dayStatus.net_profit ?? 0)} negative={(dayStatus.net_profit ?? 0) < -0.005} />
            <dl className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
              {(
                [
                  ['Cash', dayStatus.cash_sales],
                  ['Card', dayStatus.card_sales],
                  ['Online', dayStatus.online_sales],
                ] as const
              ).map(([label, v]) => (
                <div key={label} className="flex gap-1.5">
                  <dt>{label}</dt>
                  <dd className="tabular-nums text-foreground">{money.whole(v ?? 0)}</dd>
                </div>
              ))}
            </dl>
            <Button asChild variant="outline" size="sm" className="ml-auto">
              <Link to="/admin/day-close">
                <Lock className="mr-1.5 h-3.5 w-3.5" /> Day close
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <KpiStrip
        cells={[
          {
            label: 'Operating expenses',
            metric: 'operating_expenses',
            value: money.whole(opex),
            exact: money.full(opex),
            sub: (
              <>
                {pctOfSales(opex)}
                {kpis && <Delta pct={kpis.expenses_change_pct} goodWhenDown previous={money.whole(kpis.prior_period_expenses)} />}
              </>
            ),
            hint: 'Open the ledger',
            onClick: onOpenLedger,
          },
          {
            label: 'Stock purchases',
            help: stockHelp,
            value: money.whole(stock),
            exact: money.full(stock),
            sub: pctOfSales(stock),
            hint: stockChip ? `Open ${stockChip.label} in the ledger` : 'Open the ledger',
            onClick: () => (stockChip ? onDrillCategory(stockChip.slug) : onOpenLedger()),
          },
          {
            label: 'Cost of goods',
            metric: 'cogs',
            value: money.whole(cogs),
            exact: money.full(cogs),
            sub:
              pnl?.summary?.cogs_basis === 'purchases' ? (
                <>From what you bought in this period.</>
              ) : (
                <>
                  From the cost set on each item sold.{' '}
                  <span className={coverage < 50 ? 'text-warning-fg' : undefined}>{coverage.toFixed(0)}% of sales costed</span>
                </>
              ),
            hint: 'Open the items report',
            onClick: () => openReports('items'),
          },
          {
            label: refunds > 0.005 ? 'Net profit after refunds' : 'Net profit',
            metric: refunds > 0.005 ? 'net_profit_after_refunds' : 'net_profit',
            value: money.whole(result),
            exact: money.full(result),
            negative: result < -0.005,
            sub: (
              <>
                On {money.whole(sales)} of sales
                {refunds > 0.005 ? `, after ${money.whole(refunds)} refunded` : ''}
                {kpis && <Delta pct={kpis.sales_change_pct} label="sales" previous={money.whole(kpis.prior_period_sales)} />}
              </>
            ),
            hint: 'Open the profit and loss',
            onClick: onOpenPnL,
          },
        ]}
      />

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">Spend by category</h2>
          <span className="text-xs text-muted-foreground">{periodLabel}. Select a category to see its entries.</span>
        </div>
        {(summary?.count ?? 0) > 0 ? (
          <ExpenseCategoryShelf
            summary={summary}
            hideEmpty
            selectedCategory=""
            onSelectCategory={slug => (slug ? onDrillCategory(slug) : onOpenLedger())}
          />
        ) : (
          <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
            No expenses recorded in this period. Add one from the Ledger tab.
          </p>
        )}
      </section>

      <PriceChangesCard from={range.fromISO} to={range.toISO} periodLabel={periodLabel} />

      <PeriodResultChart
        title="Sales and operating expenses by day"
        description={`${periodLabel}. Completed sales against the operating expenses recorded each day. Cost of goods is not in this view; the P&L tab has it.`}
        points={hasActivity ? trend : []}
        salesLabel="Sales"
        resultLabel="Sales less expenses"
        emptyText="No sales or expenses in this period."
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">How expenses were entered</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Fact label="Entered by hand" value={String(kpis?.manual_expense_count ?? 0)} />
            <Fact
              label={
                <TermLabel
                  label="Created automatically"
                  help="Entries the system wrote from a stock purchase, a supplier return or a cost correction. They cannot be edited in the ledger."
                />
              }
              value={String(kpis?.auto_linked_expense_count ?? 0)}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base">Cash closing in this period</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => openReports('day-close')}>
              Day close history <ArrowUpRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Fact label="Days closed" value={String(intel?.cash_closing_stats?.days_with_closing ?? 0)} />
            <Fact
              label={
                <TermLabel
                  label="Average cash difference"
                  help="Counted cash less expected cash, averaged over the days closed. Negative means the drawer was short."
                />
              }
              value={money.full(intel?.cash_closing_stats?.avg_cash_difference ?? 0)}
            />
            <Fact
              label={
                <TermLabel
                  label="Total cash variance"
                  help="Every day's difference added up regardless of direction, so a short day and an over day do not cancel out."
                />
              }
              value={money.full(intel?.cash_closing_stats?.total_abs_cash_variance ?? 0)}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Fact({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  )
}

function TodayStat({ label, value, negative }: { label: string; value: string; negative?: boolean }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn('text-base font-semibold', negative && 'text-danger-fg')}>{value}</p>
    </div>
  )
}

/**
 * Change against the period before. The arrow gives the direction and the
 * tone says whether that direction is good — costs rising is not.
 */
function Delta({ pct, previous, label, goodWhenDown = false }: { pct: number; previous: string; label?: string; goodWhenDown?: boolean }) {
  const flat = Math.abs(pct) <= 0.05
  const up = pct > 0
  const good = goodWhenDown ? !up : up
  const Icon = up ? ArrowUp : ArrowDown
  return (
    <span className="mt-0.5 block">
      <span className={cn('inline-flex items-center gap-0.5 font-medium', flat ? 'text-muted-foreground' : good ? 'text-success-fg' : 'text-danger-fg')}>
        {!flat && <Icon className="h-3 w-3" aria-hidden />}
        {flat ? 'No change' : `${Math.abs(pct).toFixed(1)}%`}
      </span>{' '}
      {label ? `${label} ` : ''}against the period before ({previous})
    </span>
  )
}
